/**
 * Accountability Chart seats API + TanStack Query hooks.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import type { Seat, CreateSeatInput, UpdateSeatInput, AssignHolderInput } from '../types';

interface ListResponse {
  seats: Seat[];
}

export const seatKeys = {
  all: ['accountability', 'seats'] as const,
  list: () => [...seatKeys.all, 'list'] as const,
  detail: (id: string) => [...seatKeys.all, 'detail', id] as const,
};

export const listSeats = () => api.get<ListResponse>('/v4/seats');
export const getSeat = (id: string) => api.get<Seat>(`/v4/seats/${id}`);
export const createSeat = (input: CreateSeatInput) => api.post<Seat>('/v4/seats', input);
export const updateSeat = (id: string, input: UpdateSeatInput) => api.patch<Seat>(`/v4/seats/${id}`, input);
export const deleteSeat = (id: string) => api.delete<void>(`/v4/seats/${id}`);
export const assignHolder = (seatId: string, input: AssignHolderInput) =>
  api.post<Seat>(`/v4/seats/${seatId}/holders`, input);
export const removeHolder = (seatId: string, userId: string) =>
  api.delete<Seat>(`/v4/seats/${seatId}/holders/${userId}`);
export const setPrimaryHolder = (seatId: string, userId: string) =>
  api.put<Seat>(`/v4/seats/${seatId}/holders/${userId}/primary`, {});
export const linkTeam = (seatId: string, teamId: string) =>
  api.post<Seat>(`/v4/seats/${seatId}/teams`, { team_id: teamId });
export const unlinkTeam = (seatId: string, teamId: string) =>
  api.delete<Seat>(`/v4/seats/${seatId}/teams/${teamId}`);

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useSeats() {
  return useQuery({
    queryKey: seatKeys.list(),
    queryFn: listSeats,
  });
}

export function useSeat(id: string | null) {
  return useQuery({
    queryKey: seatKeys.detail(id ?? ''),
    queryFn: () => getSeat(id!),
    enabled: !!id,
  });
}

function invalidate(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: seatKeys.all });
}

export function useCreateSeat() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createSeat,
    onSuccess: () => { invalidate(queryClient); toast.success('Seat created'); },
    onError: (e: Error) => toast.error('Failed to create seat', { description: e.message }),
  });
}

export function useUpdateSeat() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateSeatInput }) => updateSeat(id, input),
    onSuccess: () => { invalidate(queryClient); },
    onError: (e: Error) => toast.error('Failed to update seat', { description: e.message }),
  });
}

export function useDeleteSeat() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteSeat,
    onSuccess: () => { invalidate(queryClient); toast.success('Seat deleted'); },
    onError: (e: Error) => toast.error('Failed to delete seat', { description: e.message }),
  });
}

export function useAssignHolder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ seatId, input }: { seatId: string; input: AssignHolderInput }) =>
      assignHolder(seatId, input),
    onSuccess: () => { invalidate(queryClient); toast.success('Holder assigned'); },
    onError: (e: Error) => toast.error('Failed to assign holder', { description: e.message }),
  });
}

export function useRemoveHolder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ seatId, userId }: { seatId: string; userId: string }) =>
      removeHolder(seatId, userId),
    onSuccess: () => { invalidate(queryClient); toast.success('Holder removed'); },
    onError: (e: Error) => toast.error('Failed to remove holder', { description: e.message }),
  });
}

export function useSetPrimaryHolder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ seatId, userId }: { seatId: string; userId: string }) =>
      setPrimaryHolder(seatId, userId),
    onSuccess: () => { invalidate(queryClient); toast.success('Primary seat updated'); },
    onError: (e: Error) => toast.error('Failed to set primary', { description: e.message }),
  });
}
